import { useState, useCallback, useMemo } from 'react';
import type { AbsenceRequest, CreateAbsenceRequestData } from '../types';
import { APIStorage } from '../services/apiStorage';
import { storageLogger } from '../utils/logger';

/**
 * Hook für Abwesenheitsanträge — Erstellen, Laden, Stornieren, Genehmigen, Ablehnen.
 */
export function useAbsenceRequests() {
  const storage = useMemo(() => new APIStorage(), []);
  const [myRequests, setMyRequests] = useState<AbsenceRequest[]>([]);
  const [pendingRequests, setPendingRequests] = useState<AbsenceRequest[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loadingMy, setLoadingMy] = useState(false);
  const [loadingPending, setLoadingPending] = useState(false);

  // ─── Employee ────────────────────────────────────────────────────────

  const loadMyRequests = useCallback(async (status?: string) => {
    setLoadingMy(true);
    try {
      const data = await storage.getMyAbsenceRequests(status);
      setMyRequests(data);
    } catch (err) {
      storageLogger.error('Fehler beim Laden der Anträge', err instanceof Error ? err : undefined);
    } finally {
      setLoadingMy(false);
    }
  }, [storage]);

  const createRequest = useCallback(async (data: CreateAbsenceRequestData): Promise<AbsenceRequest | null> => {
    try {
      const created = await storage.createAbsenceRequest(data);
      setMyRequests((prev) => [created, ...prev]);
      return created;
    } catch (err) {
      storageLogger.error('Fehler beim Erstellen des Antrags', err instanceof Error ? err : undefined);
      throw err;
    }
  }, [storage]);

  const cancelRequest = useCallback(async (id: string): Promise<void> => {
    try {
      const updated = await storage.cancelAbsenceRequest(id);
      setMyRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } catch (err) {
      storageLogger.error('Fehler beim Stornieren', err instanceof Error ? err : undefined);
      throw err;
    }
  }, [storage]);

  const planRequest = useCallback(async (data: CreateAbsenceRequestData): Promise<AbsenceRequest | null> => {
    try {
      const created = await storage.planAbsenceRequest(data);
      setMyRequests((prev) => [created, ...prev]);
      return created;
    } catch (err) {
      storageLogger.error('Fehler beim Vormerken', err instanceof Error ? err : undefined);
      throw err;
    }
  }, [storage]);

  const submitPlannedRequest = useCallback(async (id: string, employeeNote?: string | null): Promise<AbsenceRequest | null> => {
    try {
      const updated = await storage.submitPlannedRequest(id, employeeNote);
      setMyRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));
      return updated;
    } catch (err) {
      storageLogger.error('Fehler beim Einreichen der vorgemerkten Abwesenheit', err instanceof Error ? err : undefined);
      throw err;
    }
  }, [storage]);

  // ─── Manager/HR ──────────────────────────────────────────────────────

  const loadPendingRequests = useCallback(async () => {
    setLoadingPending(true);
    try {
      const data = await storage.getPendingAbsenceRequests();
      setPendingRequests(data);
    } catch (err) {
      storageLogger.error('Fehler beim Laden der offenen Anträge', err instanceof Error ? err : undefined);
    } finally {
      setLoadingPending(false);
    }
  }, [storage]);

  const loadPendingCount = useCallback(async () => {
    try {
      const { count } = await storage.getPendingAbsenceRequestsCount();
      setPendingCount(count);
    } catch {
      // Badge-Count ist nicht kritisch
    }
  }, [storage]);

  const approveRequest = useCallback(async (id: string, note?: string): Promise<void> => {
    try {
      await storage.approveAbsenceRequest(id, note);
      setPendingRequests((prev) => prev.filter((r) => r.id !== id));
      setPendingCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      storageLogger.error('Fehler beim Genehmigen', err instanceof Error ? err : undefined);
      throw err;
    }
  }, [storage]);

  const rejectRequest = useCallback(async (id: string, note: string): Promise<void> => {
    try {
      await storage.rejectAbsenceRequest(id, note);
      setPendingRequests((prev) => prev.filter((r) => r.id !== id));
      setPendingCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      storageLogger.error('Fehler beim Ablehnen', err instanceof Error ? err : undefined);
      throw err;
    }
  }, [storage]);

  return {
    // State
    myRequests,
    pendingRequests,
    pendingCount,
    loading: loadingMy || loadingPending,
    loadingMy,
    loadingPending,
    // Employee
    loadMyRequests,
    createRequest,
    cancelRequest,
    planRequest,
    submitPlannedRequest,
    // Manager/HR
    loadPendingRequests,
    loadPendingCount,
    approveRequest,
    rejectRequest,
  };
}
